// src/pages/console/tokenInfo.tsx

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { ApiResponse } from '../../models/apiResponse';
import { TokenResult } from '../../models/token';
import { fetchTokenData } from '../../services/authService';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

const TokenInfo: React.FC = () => {
  const [tokenData, setTokenData] = useState<TokenResult | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const [showSecretKey, setShowSecretKey] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data: ApiResponse<TokenResult> = await fetchTokenData();
      setTokenData(data.result);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch token data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCopyKey = () => {
    if (tokenData?.secretKey) {
      navigator.clipboard.writeText(tokenData.secretKey);
      setToastOpen(true);
    }
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString('ko-KR');
  };

  const toggleSecretKey = () => {
    setShowSecretKey(!showSecretKey);
  };

  const maskSecretKey = (key: string) => {
    return showSecretKey ? key : '*'.repeat(key.length);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>
        토큰 정보
      </Typography>

      <Alert 
        severity="info" 
        sx={{ 
          mb: 4,
          borderRadius: 2,
          '& .MuiAlert-message': {
            fontWeight: 'medium'
          }
        }}
      >
        토큰은 매일 오전 0시(UTC)에 자동으로 초기화됩니다.
      </Alert>

      <Card elevation={0} sx={{ mb: 4, bgcolor: 'white', borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Secret Key
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body1" sx={{ flex: 1, fontFamily: 'monospace' }}>
              {maskSecretKey(tokenData?.secretKey || '')}
            </Typography>
            <Tooltip title={showSecretKey ? "숨기기" : "표시"}>
              <IconButton onClick={toggleSecretKey} size="small">
                {showSecretKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="복사">
              <IconButton onClick={handleCopyKey} size="small">
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary">
            API 호출 시 필요한 인증 키입니다.
          </Typography>
        </CardContent>
      </Card>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ bgcolor: 'white', borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                전체 토큰
              </Typography>
              <Typography variant="h4" color="primary.main">
                {formatNumber(tokenData?.totalTokens || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ bgcolor: 'white', borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                사용 토큰
              </Typography>
              <Typography variant="h4" color="primary.main">
                {formatNumber(tokenData?.usedTokens || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ bgcolor: 'white', borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                남은 토큰
              </Typography>
              <Typography variant="h4" color="primary.main">
                {formatNumber(tokenData?.remainingTokens || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card 
        elevation={0} 
        sx={{ 
          bgcolor: 'white', 
          borderRadius: 2,
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          p: 2
        }}
      >
        <Typography variant="body2" color="textSecondary">
          마지막 갱신: {tokenData?.lastRenewal ? format(new Date(tokenData.lastRenewal), 'yyyy.MM.dd HH:mm:ss', { locale: ko }) : '정보 없음'}
        </Typography>
      </Card>

      <Typography 
        variant="body2" 
        color="textSecondary"
        sx={{ 
          mt: 'auto',
          textAlign: 'center',
          fontStyle: 'italic'
        }}
      >
        * SecretKey 변경 기타 문의사항은 kyulabs@kyulabs.app로 연락주세요.
      </Typography>

      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        message="클립보드에 복사되었습니다."
      />
    </Box>
  );
};

export default TokenInfo;